import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { t } from 'i18next';
import { Button } from 'ncoded-component-library';
import MymeTypeIcon from '../../../../MymeTypeIcon';
import { useNavigate } from 'react-router-dom';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import ChooseAFolderInDr from 'router/subrouters/DataRoom/components/ChooseAFolderInDr';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { getDataRoomRoute, getIsNodeFolder, getNodeMimeType } from 'utils';
import { isMaximumSelected } from 'router/subrouters/DataRoom/utils';

import './MoveFilesToFolder.styles.scss';
import './MoveFilesToFolder.styles.responsive.scss';

export type MoveFilesToFolderProps = {
  isOpen?: boolean;
  nodes: DataRoomNode[];
  documentId: string;
  nodeToMoveTo?: string;
  setNodeToMoveTo: (node: string) => void;
  closeMoveFiles?: () => void;
};

const MoveFilesToFolder: FC<MoveFilesToFolderProps> = ({
  nodes = [],
  isOpen,
  documentId,
  nodeToMoveTo,
  setNodeToMoveTo,
  closeMoveFiles,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [folderName, setFolderName] = useState('');

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const selectedNumOfNodes = nodes.length;
  const isSingularNode = selectedNumOfNodes === 1 && nodes[0];
  const disabledSubmit = nodeToMoveTo === documentId || isLoading;

  const excludeFolderIds = useMemo(
    () =>
      nodes
        .filter(getIsNodeFolder)
        .map(({ _id }) => _id)
        .join(','),
    [nodes],
  );

  const navigate = useNavigate();

  const onChooseAFolderInDrChange = useCallback(
    (nodeId, folderName) => {
      setNodeToMoveTo(nodeId);
      setFolderName(folderName);
    },
    [setNodeToMoveTo],
  );

  const onSubmit = useCallback(async () => {
    if (disabledSubmit) return;

    const filesIds = nodes.map(({ _id }) => _id);

    try {
      setIsLoading(true);
      await dataRoomApi.nodes.moveFilesToFolder(filesIds, nodeToMoveTo);

      setIsLoading(false);
      closeMoveFiles?.();
      popSuccess(
        t('successfullyMovedFilesToThisFolder', {
          count: selectedNumOfNodes,
          folderName,
        }),
      );
      navigate(
        `${getDataRoomRoute(dataRoomId, 'documents')}/${nodeToMoveTo || ''}`,
      );
    } catch (e) {
      setIsLoading(false);
      popServerError(e);
    }
  }, [
    dataRoomId,
    disabledSubmit,
    folderName,
    selectedNumOfNodes,
    nodes,
    nodeToMoveTo,
    closeMoveFiles,
    popServerError,
    popSuccess,
    navigate,
  ]);

  return (
    <>
      <div className="move-files-to-folder__title">
        {isSingularNode && (
          <p className="move-files-to-folder__title__single-file">
            <MymeTypeIcon
              mimeType={getNodeMimeType(nodes[0])}
              isLocked={isMaximumSelected(nodes[0]?.fileSecurity)}
            />
            {nodes[0].name}
          </p>
        )}

        <p>{t('chooseNewDestination', { count: selectedNumOfNodes })}</p>
      </div>

      <div className="move-files-to-folder__folders">
        <ChooseAFolderInDr
          hasCreateFolder
          disabledFetch={!isOpen}
          dataRoomId={dataRoomId}
          currenntlySelectedNodeId={nodeToMoveTo}
          excludeFolderIds={excludeFolderIds}
          onChange={onChooseAFolderInDrChange}
        />
      </div>

      <div className="move-files-to-folder__actions">
        <Button variant="link" onClick={closeMoveFiles}>
          {t('cancel')}
        </Button>
        <Button type="submit" disabled={disabledSubmit} onClick={onSubmit}>
          {t('moveItems', { count: selectedNumOfNodes })}
        </Button>
      </div>
    </>
  );
};

export default MoveFilesToFolder;
